import React, { useEffect, useMemo, useState } from 'react';
import { fetchSupplierReturns, fetchSuppliers, deleteSupplierReturn } from '@/api';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Eye, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ViewReturnDialog } from './ViewReturnDialog';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import type { SupplierReturn, Supplier } from '@/types/supplier';

interface Props {
  supplierId?: string | null;
}

export function ReturnsTable({ supplierId }: Props) {
  const currencySymbol = useCurrencySymbol();
  const [returns, setReturns] = useState<SupplierReturn[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [filterSupplier, setFilterSupplier] = useState<string>(supplierId || 'All');
  const [viewReturnId, setViewReturnId] = useState<string | null>(null);
  const [pagination, setPagination] = useState({ current_page: 1, per_page: 20 });

  const load = () => {
    fetchSupplierReturns()
      .then(res => setReturns(res.data || []))
      .catch(() => toast.error('Failed to load returns'));
  };

  useEffect(() => {
    load();
    fetchSuppliers().then(res => setSuppliers(res.data || [])).catch(() => setSuppliers([]));
  }, []);

  useEffect(() => {
    setFilterSupplier(supplierId || 'All');
  }, [supplierId]);

  const filtered = useMemo(() => {
    if (filterSupplier === 'All') return returns;
    return returns.filter(r => r.supplier_id === filterSupplier);
  }, [returns, filterSupplier]);

  const total = filtered.length;
  const last_page = Math.max(1, Math.ceil(total / pagination.per_page));
  const paged = useMemo(() => {
    const start = (pagination.current_page - 1) * pagination.per_page;
    return filtered.slice(start, start + pagination.per_page);
  }, [filtered, pagination]);

  const totalCredited = useMemo(() => filtered.reduce((sum, r) => sum + Number(r.total_amount), 0), [filtered]);

  const handleDelete = async (r: SupplierReturn) => {
    if (!confirm(`Undo return ${r.return_number}? This restores the returned stock and removes the credit it added.`)) return;
    try {
      await deleteSupplierReturn(r.id);
      toast.success('Return undone — stock and credit restored');
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to undo return');
    }
  };

  const formatDate = (date: string) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(date || '');
    if (match) {
      const [, year, month, day] = match;
      return `${month}/${day}/${year}`;
    }
    return date;
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card>
          <CardHeader>
            <CardTitle>Total Returned</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{currencySymbol}{totalCredited.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {filtered.length} return{filtered.length !== 1 ? 's' : ''}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Filter by Supplier</CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={filterSupplier} onValueChange={(v) => { setFilterSupplier(v); setPagination(p => ({ ...p, current_page: 1 })); }}>
              <SelectTrigger>
                <SelectValue placeholder="All suppliers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Suppliers</SelectItem>
                {suppliers.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Return #</TableHead>
            <TableHead>Supplier</TableHead>
            <TableHead>Against Invoice</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Reason</TableHead>
            <TableHead className="text-right">Credited</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paged.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                No returns recorded{filterSupplier !== 'All' ? ' for this supplier' : ''}.
              </TableCell>
            </TableRow>
          ) : (
            paged.map(r => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">{r.return_number}</TableCell>
                <TableCell>{r.supplier_name || '—'}</TableCell>
                <TableCell>
                  {r.invoice_number
                    ? <Badge variant="outline">{r.invoice_number}</Badge>
                    : <Badge variant="secondary">Bulk return</Badge>}
                </TableCell>
                <TableCell>{formatDate(r.date)}</TableCell>
                <TableCell className="text-muted-foreground">{r.reason || '—'}</TableCell>
                <TableCell className="text-right text-green-600 font-medium">
                  {currencySymbol}{Number(r.total_amount).toFixed(2)}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-purple-500 text-purple-500 hover:bg-purple-50"
                      onClick={() => setViewReturnId(r.id)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-red-500 text-red-500 hover:bg-red-50"
                      onClick={() => handleDelete(r)}
                      title="Undo this return"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <div className="flex items-center justify-between py-3">
        <div className="text-sm text-muted-foreground">
          {(() => {
            if (!total) return 'Showing 0-0 of 0 returns';
            const start = (pagination.current_page - 1) * pagination.per_page + (paged.length ? 1 : 0);
            const end = (pagination.current_page - 1) * pagination.per_page + paged.length;
            return `Showing ${start}-${end} of ${total} returns`;
          })()}
        </div>
        {last_page > 1 && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPagination(p => ({ ...p, current_page: p.current_page - 1 }))}
              disabled={pagination.current_page <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPagination(p => ({ ...p, current_page: p.current_page + 1 }))}
              disabled={pagination.current_page >= last_page}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      <ViewReturnDialog
        open={!!viewReturnId}
        onOpenChange={(open) => !open && setViewReturnId(null)}
        returnId={viewReturnId}
      />
    </>
  );
}
